import { forwardRef, useEffect, useRef, useState } from 'react'
import { IconContext } from 'react-icons'
import { AiOutlineEye, AiOutlineEyeInvisible, AiOutlineInfoCircle } from 'react-icons/ai'
import Tooltip from 'components/Ui/Tooltip'

export default forwardRef(function TextInput(
    {
        type = 'text',
        id = '',
        label = '',
        placeholder = '',
        className = '',
        icon = '',
        error = '',
        message = '',
        leftSpan = false,
        isFocused = false,
        password = false,
        tooltip = '',
        ...props
    },
    ref
) {
    const [secureText, setSecureText] = useState(password)
    const input = useRef()
    const errorId = id ? `${id}-error` : undefined

    useEffect(() => {
        if (isFocused) {
            input.current.focus()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className={className}>
            {label && (
                <label htmlFor={id} className={`mb-1 font-medium inline-flex items-center gap-1 ${error ? 'text-danger' : 'text-black dark:text-white'}`}>
                    {label}
                    {tooltip && (
                        <Tooltip title={tooltip} wide>
                            <AiOutlineInfoCircle size={16} className="text-bodydark2 cursor-help" aria-hidden="true" />
                        </Tooltip>
                    )}
                </label>
            )}
            <div className="relative flex items-stretch flex-nowrap">
                {leftSpan && (
                    <span className="flex items-center px-3 text-black border border-r-0 rounded-lg rounded-r-none border-stroke bg-whiter dark:bg-white/30 dark:text-white dark:border-form">
                        {leftSpan}
                    </span>
                )}
                <input
                    {...props}
                    type={secureText ? 'password' : type}
                    id={id}
                    placeholder={label ? label : placeholder}
                    aria-invalid={!!error}
                    aria-describedby={error && errorId ? errorId : undefined}
                    className={`${leftSpan && 'rounded-l-none'} w-full flex-auto rounded-lg border bg-transparent py-2 pl-3 ${
                        (password || icon) && 'pr-9'
                    } ${error ? 'border-danger focus:ring-danger focus:border-danger' : 'border-stroke focus:ring-primary focus:border-primary dark:border-form dark:bg-input dark:focus:ring-secondary dark:focus:border-secondary'} outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-whiter dark:disabled:bg-black`}
                    ref={ref || input}
                />
                {password && (
                    <button
                        type="button"
                        aria-label={secureText ? 'Show password' : 'Hide password'}
                        aria-pressed={!secureText}
                        onClick={() => setSecureText(e => !e)}
                        className="absolute -translate-y-1/2 top-1/2 right-4 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <IconContext.Provider value={{ size: '22' }}>
                            <span aria-hidden="true">
                                {secureText ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                            </span>
                        </IconContext.Provider>
                    </button>
                )}
                {icon && (
                    <span aria-hidden="true" className="absolute -translate-y-1/2 top-1/2 right-4">
                        <IconContext.Provider value={{ color: '#c4cad3', size: '22' }}>{icon}</IconContext.Provider>
                    </span>
                )}
            </div>
            {error && (
                <p id={errorId} role="alert" className="text-sm text-danger">
                    {message}
                </p>
            )}
        </div>
    )
})
